// ---------------------------------------------------------------------------
// Deal engine (Sales Intelligence, phase 1): risks with severity, the Next Best Action, Expected PO
// intelligence, stage aging and the completeness score of an open deal. Pure functions: every result
// carries its reasons ("Why?"). The deal's sales stages are only read, never changed.
// ---------------------------------------------------------------------------
import { z } from 'zod';
import { QUOTE_SYSTEMS, READINESS_ITEMS, READINESS_STATUSES, type ReadinessItem } from './quotedBrands';
import { isoDate } from './validation';

export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Severity = (typeof SEVERITIES)[number];
export const SEVERITY_LABEL: Record<Severity, string> = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
const rank = (s: Severity) => SEVERITIES.indexOf(s);

export const RISK_CATEGORIES = {
  activity: 'Activity', stage: 'Stage', commercial: 'Commercial', technical: 'Technical',
  relationship: 'Relationship', timeline: 'Timeline',
} as const;
export type RiskCategory = keyof typeof RISK_CATEGORIES;

/** Every risk the engine detects, its category and default severity (administrators can change it or turn it off). */
export const RISK_RULES: { key: string; category: RiskCategory; label: string; severity: Severity }[] = [
  { key: 'no_activity', category: 'activity', label: 'No customer activity', severity: 'high' },
  { key: 'no_quote_followup', category: 'activity', label: 'No follow-up after the quotation', severity: 'high' },
  { key: 'no_next_action', category: 'activity', label: 'No next action', severity: 'medium' },
  { key: 'next_action_overdue', category: 'activity', label: 'Next action date overdue', severity: 'medium' },
  { key: 'stage_attention', category: 'stage', label: 'Long time in the current stage', severity: 'medium' },
  { key: 'stage_stalled', category: 'stage', label: 'Stalled in the current stage', severity: 'high' },
  { key: 'moving_backward', category: 'stage', label: 'Moved back to earlier stages', severity: 'medium' },
  { key: 'close_overdue', category: 'stage', label: 'Close date passed, deal still open', severity: 'high' },
  { key: 'low_margin', category: 'commercial', label: 'Margin below target', severity: 'high' },
  { key: 'value_drop', category: 'commercial', label: 'Deal value dropped significantly', severity: 'medium' },
  { key: 'approval_pending', category: 'commercial', label: 'Approval pending', severity: 'medium' },
  { key: 'quote_expired', category: 'commercial', label: 'Quotation expired', severity: 'medium' },
  { key: 'gp_approval', category: 'commercial', label: 'Quotation GP needs approval', severity: 'high' },
  { key: 'margin_erosion', category: 'commercial', label: 'Quotation GP dropping between revisions', severity: 'medium' },
  { key: 'excessive_discount', category: 'commercial', label: 'Large discount from the original quotation', severity: 'medium' },
  { key: 'too_many_revisions', category: 'commercial', label: 'Many quotation revisions', severity: 'low' },
  { key: 'submittal_revise', category: 'technical', label: 'Submittal returned (C / rejected)', severity: 'high' },
  { key: 'submittal_overdue', category: 'technical', label: 'Consultant reply on a submittal overdue', severity: 'medium' },
  { key: 'submittal_requested', category: 'technical', label: 'Customer asked for submittals: not ready yet', severity: 'medium' },
  { key: 'submittal_request_late', category: 'technical', label: 'Customer’s submittal request past its date', severity: 'high' },
  { key: 'clarification', category: 'technical', label: 'Pre-sales clarification unresolved', severity: 'medium' },
  { key: 'no_documents', category: 'technical', label: 'Tender documents missing', severity: 'medium' },
  { key: 'design_incomplete', category: 'technical', label: 'System design incomplete', severity: 'medium' },
  { key: 'vendor_quotes_missing', category: 'technical', label: 'Vendor quotations missing', severity: 'medium' },
  { key: 'clarifications_open', category: 'technical', label: 'Technical clarifications unresolved', severity: 'medium' },
  { key: 'no_decision_maker', category: 'relationship', label: 'No decision maker identified', severity: 'medium' },
  { key: 'weak_relationship', category: 'relationship', label: 'Weak customer relationship', severity: 'medium' },
  { key: 'negative_sentiment', category: 'relationship', label: 'Negative customer sentiment', severity: 'high' },
  { key: 'po_overdue', category: 'timeline', label: 'Expected PO date passed', severity: 'high' },
  { key: 'po_severely_overdue', category: 'timeline', label: 'Expected PO date severely overdue', severity: 'critical' },
  { key: 'no_po_date', category: 'timeline', label: 'No expected PO date', severity: 'low' },
  { key: 'tender_soon', category: 'timeline', label: 'Tender deadline approaching, quotation not submitted', severity: 'high' },
  { key: 'tender_critical', category: 'timeline', label: 'Tender deadline in days, quotation not submitted', severity: 'critical' },
  { key: 'internal_deadline_overdue', category: 'timeline', label: 'Internal quotation deadline passed', severity: 'high' },
  { key: 'tender_not_ready', category: 'timeline', label: 'Tender at risk: package not ready for the deadline', severity: 'high' },
  { key: 'po_postponed', category: 'timeline', label: 'Expected PO repeatedly postponed', severity: 'medium' },
  { key: 'engagement_declining', category: 'activity', label: 'Customer engagement declining', severity: 'medium' },
  { key: 'weak_information', category: 'stage', label: 'Large deal with weak information', severity: 'medium' },
];
export const riskRule = (key: string) => RISK_RULES.find((r) => r.key === key);
/** The deal field a recommendation is fixed in: Accept opens the deal's edit form on it. */
export const ACTION_FIELD: Record<string, string> = {
  no_decision_maker: 'decisionMakerId', weak_relationship: 'relationshipStrength', negative_sentiment: 'customerSentiment',
  no_next_action: 'nextStep', next_action_overdue: 'nextActionDate', close_overdue: 'closeDate', no_po_date: 'expectedPoDate', po_overdue: 'expectedPoDate',
  po_severely_overdue: 'expectedPoDate', po_postponed: 'expectedPoDate', confirm_budget: 'budgetConfirmed',
  low_margin: 'expectedMargin',
};

export const STAGE_AGING = [
  { value: 'normal', label: 'Normal' },
  { value: 'slower', label: 'Slower than average' },
  { value: 'attention', label: 'Needs attention' },
  { value: 'stalled', label: 'Stalled' },
] as const;
export type StageAgingStatus = (typeof STAGE_AGING)[number]['value'];

/** Fields the completeness score can check, and when each one is expected by default. */
export const COMPLETENESS_WEIGHTS = [
  { value: 'critical', label: 'Critical', points: 3 }, { value: 'recommended', label: 'Recommended', points: 2 }, { value: 'optional', label: 'Optional', points: 1 },
] as const;
export type CompletenessWeight = (typeof COMPLETENESS_WEIGHTS)[number]['value'];
const W_POINTS: Record<CompletenessWeight, number> = { critical: 3, recommended: 2, optional: 1 };
export const COMPLETENESS_BANDS = [
  { value: 'excellent', label: 'Excellent' }, { value: 'good', label: 'Good' }, { value: 'improve', label: 'Needs improvement' }, { value: 'poor', label: 'Poor' },
] as const;
export type CompletenessBand = (typeof COMPLETENESS_BANDS)[number]['value'];
export const COMPLETENESS_FIELDS: { key: string; label: string; when: CompletenessWhen; weight: CompletenessWeight }[] = [
  { key: 'accountId', label: 'Customer', when: 'always', weight: 'critical' },
  { key: 'amount', label: 'Opportunity value', when: 'always', weight: 'critical' },
  { key: 'closeDate', label: 'Close date', when: 'always', weight: 'recommended' },
  { key: 'expectedPoDate', label: 'Expected PO date', when: 'direct', weight: 'critical' },
  { key: 'nextStep', label: 'Next action', when: 'always', weight: 'critical' },
  { key: 'nextActionDate', label: 'Next action date', when: 'always', weight: 'recommended' },
  { key: 'decisionMakerId', label: 'Decision maker', when: 'always', weight: 'critical' },
  { key: 'consultantId', label: 'Consultant', when: 'tender', weight: 'recommended' },
  { key: 'mainContractorId', label: 'Main contractor', when: 'tender', weight: 'recommended' },
  { key: 'endUserId', label: 'End user', when: 'tender', weight: 'optional' },
  { key: 'quotedBrands', label: 'Quoted brand', when: 'quoted', weight: 'recommended' },
  { key: 'quotation', label: 'Quotation', when: 'quoted', weight: 'critical' },
  { key: 'expectedMargin', label: 'Expected GP %', when: 'quoted', weight: 'critical' },
  { key: 'submissionDeadline', label: 'Tender deadline', when: 'tender', weight: 'critical' },
  { key: 'documents', label: 'Important documents', when: 'always', weight: 'recommended' },
  { key: 'recentActivity', label: 'Recent activity', when: 'always', weight: 'recommended' },
];
export const COMPLETENESS_WHEN = [
  { value: 'always', label: 'Every open deal' },
  { value: 'direct', label: 'Every open deal, not tenders' },
  { value: 'quoted', label: 'From the quotation stage' },
  { value: 'quoted_direct', label: 'From the quotation stage, not tenders' },
  { value: 'tender', label: 'Tenders only' },
  { value: 'off', label: 'Not checked' },
] as const;
export type CompletenessWhen = (typeof COMPLETENESS_WHEN)[number]['value'];

// ---------------------------------------------------------------------------
// Completeness impact classification: separates "data completeness gaps" from "risk triggers"
// for each completeness field. A missing "risk" field actively increases commercial risk; a
// missing "completeness" field reduces data quality but does not, by itself, force red health.
// ---------------------------------------------------------------------------
export type CompletenessImpactType = 'risk' | 'completeness';
export interface CompletenessImpact {
  field: string;
  category: CompletenessWeight;
  impact: CompletenessImpactType;
  label: string;
}

// ---------------------------------------------------------------------------
// Settings (Setup → Intelligence). Stored per company; anything missing uses these defaults.
// ---------------------------------------------------------------------------
export interface IntelSettings {
  idleDays: number;
  quoteFollowUpDays: number;
  poSevereDays: number;
  poChangesWarn: number;
  tenderWarnDays: number;
  tenderCriticalDays: number;
  stageDefaults: { attention: number; stalled: number };
  /** Per existing stage (by its id): its own limits. */
  stages: Record<string, { attention?: number; stalled?: number }>;
  /** Changed severities; 'off' turns a risk off. */
  severity: Record<string, Severity | 'off'>;
  completeness: Record<string, CompletenessWhen>;
  notifyCritical: boolean;
  /** Pre-sales workload points (1 per open deal + 2 per deadline this week, overdue deadline, high-risk deal) for High / Overloaded. */
  workloadHigh: number;
  workloadOverloaded: number;
  /** Quotation GP limits: from gpNormal no approval; from gpManager a sales manager; from gpManagement administrators; below, critical. */
  gpNormal: number;
  gpManager: number;
  gpManagement: number;
  maxRevisions: number;
  discountWarnPct: number;
  gpErosionPts: number;
  /** Days before a tender deadline when the salesperson and pre-sales are reminded. */
  tenderReminderDays: number[];
  /** Weight of each item in a system's technical readiness. */
  readinessWeights: Record<ReadinessItem, number>;
  /** Days before a tool's calibration due date when its holder and the project managers are reminded. */
  toolReminderDays: number[];
  /** Weight of each completeness item (Critical 3, Recommended 2, Optional 1). */
  completenessWeights: Record<string, CompletenessWeight>;
  /** Completeness from these scores: Excellent, Good, Needs improvement; below: Poor. */
  completenessBands: { excellent: number; good: number; improve: number };
  /** Deals from this value get management-intervention recommendations and the large-deal checks. */
  mgmtValue: number;
  /** Purchasing: a brand's price rising more than this % (against earlier purchases) is flagged. */
  priceChangePct: number;
  /** Purchasing: a project's committed cost reaching this % of the cost pre-sales assumed is warned (red above 100%). */
  budgetWarnPct: number;
  /** Cash flow: when a supplier has no payment terms (record or quotation), pay this many days after the invoice. */
  supplierPayDays: number;
  /** Cash flow: customers are expected to pay this many days after an invoice. */
  customerPayDays: number;
  /** Quotation follow-up: tasks for the salesperson this many days after the quotation is submitted. */
  followUpEnabled: boolean;
  followUpDays: number[];
  /** "Quotation valid until" when it is empty: the submitted date + these days; one more task this many days before it ends. */
  quoteValidityDays: number;
  expiryReminderDays: number;
  /** A follow-up task overdue this many days: the salesperson's manager and the CEO are told (once). */
  followUpEscalateDays: number;
}
export const INTEL_DEFAULTS: IntelSettings = {
  idleDays: 14, quoteFollowUpDays: 7, poSevereDays: 21, poChangesWarn: 2, tenderWarnDays: 7, tenderCriticalDays: 2,
  stageDefaults: { attention: 30, stalled: 45 }, stages: {}, severity: {}, completeness: {}, notifyCritical: true,
  workloadHigh: 12, workloadOverloaded: 20,
  gpNormal: 18, gpManager: 15, gpManagement: 12, maxRevisions: 3, discountWarnPct: 10, gpErosionPts: 2,
  tenderReminderDays: [14, 7, 3, 1],
  readinessWeights: { boq: 15, design: 15, drawings: 10, compliance: 10, technical: 20, vendorQuote: 15, costing: 15 },
  toolReminderDays: [30, 14, 7],
  completenessWeights: {}, completenessBands: { excellent: 90, good: 75, improve: 50 }, mgmtValue: 1_000_000,
  priceChangePct: 10, budgetWarnPct: 90, supplierPayDays: 30, customerPayDays: 30,
  followUpEnabled: true, followUpDays: [3, 7, 14], quoteValidityDays: 30, expiryReminderDays: 5, followUpEscalateDays: 3,
};
const days = z.number().int().min(1).max(730);
const sev = z.enum(['low', 'medium', 'high', 'critical', 'off']);
export const intelSettingsSchema = z.object({
  idleDays: days, quoteFollowUpDays: days, poSevereDays: days, poChangesWarn: z.number().int().min(1).max(20),
  tenderWarnDays: days, tenderCriticalDays: days,
  stageDefaults: z.object({ attention: days, stalled: days }),
  stages: z.record(z.string().uuid(), z.object({ attention: days.optional(), stalled: days.optional() })),
  severity: z.record(z.string().max(40), sev),
  completeness: z.record(z.string().max(40), z.enum(['always', 'direct', 'quoted', 'quoted_direct', 'tender', 'off'])),
  notifyCritical: z.boolean(),
  workloadHigh: z.number().int().min(1).max(500),
  workloadOverloaded: z.number().int().min(2).max(500),
  gpNormal: z.number().min(-100).max(100), gpManager: z.number().min(-100).max(100), gpManagement: z.number().min(-100).max(100),
  maxRevisions: z.number().int().min(1).max(50), discountWarnPct: z.number().min(0).max(100), gpErosionPts: z.number().min(0).max(100),
  tenderReminderDays: z.array(z.number().int().min(1).max(90)).max(8),
  toolReminderDays: z.array(z.number().int().min(1).max(180)).max(8),
  completenessWeights: z.record(z.string().max(40), z.enum(['critical', 'recommended', 'optional'])),
  completenessBands: z.object({ excellent: z.number().min(1).max(100), good: z.number().min(1).max(100), improve: z.number().min(1).max(100) }),
  mgmtValue: z.number().min(0).max(1e12),
  priceChangePct: z.number().min(1).max(500), budgetWarnPct: z.number().min(10).max(100),
  supplierPayDays: z.number().int().min(0).max(365), customerPayDays: z.number().int().min(0).max(365),
  followUpEnabled: z.boolean(), followUpDays: z.array(z.number().int().min(1).max(90)).min(1).max(6),
  quoteValidityDays: z.number().int().min(1).max(365), expiryReminderDays: z.number().int().min(1).max(60), followUpEscalateDays: z.number().int().min(1).max(30),
  readinessWeights: z.object(Object.fromEntries(READINESS_ITEMS.map((r) => [r.key, z.number().min(0).max(100)])) as Record<ReadinessItem, z.ZodNumber>),
}).partial().strict();
/**
 * Saved settings over the defaults, key by key: a value that no longer fits (a setting renamed, tightened or extended
 * by an update) falls back to its default alone, and the valid parts of a nested setting are kept, so one stale key
 * can't reset every setting (GP limits, stage limits, severities) to the defaults.
 */
export function mergeIntelSettings(saved: unknown): IntelSettings {
  const out: Record<string, unknown> = { ...INTEL_DEFAULTS };
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return out as unknown as IntelSettings;
  const shape = intelSettingsSchema.shape as Record<string, z.ZodTypeAny>;
  for (const [key, value] of Object.entries(saved as Record<string, unknown>)) {
    const field = shape[key];
    if (!field) continue;
    const whole = field.safeParse(value);
    if (whole.success) { if (whole.data !== undefined) out[key] = whole.data; continue; }
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const inner = field instanceof z.ZodOptional ? field.unwrap() : field;
    if (inner instanceof z.ZodObject) {
      const parts = Object.entries(value as Record<string, unknown>).filter(([k, v]) => (inner.shape as Record<string, z.ZodTypeAny>)[k]?.safeParse(v).success);
      out[key] = { ...(out[key] as object), ...Object.fromEntries(parts) };
    } else if (inner instanceof z.ZodRecord) {
      out[key] = Object.fromEntries(Object.entries(value as Record<string, unknown>).filter(([k, v]) => inner.keySchema.safeParse(k).success && inner.valueSchema.safeParse(v).success));
    }
  }
  return out as unknown as IntelSettings;
}

// ---------------------------------------------------------------------------
// Engine input / output
// ---------------------------------------------------------------------------
export interface EngineInput {
  today: string;
  amount: number | null;
  stageId: string;
  stageName: string;
  /** The current stage is the quotation stage or later. */
  stageQuoted: boolean;
  daysInStage: number | null;
  /** Company average days deals spend in this stage (from stage history), when known. */
  stageAvgDays: number | null;
  ownerStageAvgDays: number | null;
  /** Average days deals still need after this stage until they are won (from stage history). */
  remainingStageDays: number | null;
  backwardMoves: number;
  daysSinceActivity: number | null;
  daysSinceCreated: number;
  quoteSubmitted: boolean;
  daysSinceQuote: number | null;
  quoteExpired: boolean;
  pendingApproval: boolean;
  nextStep: string | null;
  nextActionDate: string | null;
  closeDate: string | null;
  expectedPoDate: string | null;
  originalPoDate: string | null;
  poChanges: number;
  /** Average days the owner's won deals came after their expected PO date (positive = late); null under 3 deals. */
  ownerPoSlipDays: number | null;
  previousPoConfidence: number | null;
  decisionMakerId: string | null;
  relationshipStrength: string | null;
  customerSentiment: string | null;
  budgetConfirmed: boolean | null;
  expectedMargin: number | null;
  targetMargin: number;
  /** Biggest drop of the deal value in the last 30 days, as a fraction (0.2 = 20%); null when none. */
  valueDrop: number | null;
  isTender: boolean;
  tenderDeadline: string | null;
  internalDeadline: string | null;
  presalesStatus: string | null;
  hasDocuments: boolean;
  submittalsToRevise: number;
  submittalsOverdue: number;
  /** The customer asked for submittals (a request from sales, or submittals recorded): a strong buying signal. */
  submittalsRequested?: boolean;
  /** Customer requests pre-sales hasn't marked ready yet, and how many are past the date asked. */
  submittalRequestsOpen?: number;
  submittalRequestsLate?: number;
  /** Fields filled in, for the completeness score (keys of COMPLETENESS_FIELDS). */
  filled: Record<string, boolean>;
  /** Our Quoted Brands lines (every revision), for quotation GP and technical readiness. */
  lines: EngineLine[];
  currentRevision: string | null;
  /** Revisions in the order they were started. */
  revisionOrder: string[];
  /** Latest approval of the current revision, if any. */
  approval: { level: string; status: string } | null;
  /** Positive signals found in recent activities (e.g. "Customer asks for a final discount"). */
  signals?: string[];
  /** Customer activities in the last 30 days and the 30 days before. */
  engagement?: { last30: number; prev30: number };
}
export interface EngineLine {
  system: string; brand: string; offerType: string; revision: string; confirmed: boolean; packageValue: number | null; gpPct: number | null;
  technicalApproval: string | null; boqStatus: string | null; designStatus: string | null; drawingsStatus: string | null; complianceStatus: string | null;
  vendorQuoteStatus: string | null; costingStatus: string | null; clarificationsOpen: number;
}
export type ApprovalLevel = 'none' | 'manager' | 'management' | 'critical';
export const APPROVAL_LEVEL_LABEL: Record<ApprovalLevel, string> = { none: 'No approval needed', manager: 'Sales manager approval', management: 'Management approval', critical: 'Critical approval (management)' };

export type Responsible = 'owner' | 'presales';
export interface Why { text: string; effect?: string; good?: boolean }
export interface DetectedRisk { key: string; category: RiskCategory; severity: Severity; title: string; description: string; action: string; responsible: Responsible }
export interface RecommendedAction { key: string; action: string; reason: string; priority: Severity; dueInDays: number; responsible: Responsible; evidence?: string; management?: boolean }
export interface EngineResult {
  risks: DetectedRisk[];
  overallRisk: Severity;
  overallWhy: Why[];
  positives: Why[];
  actions: RecommendedAction[];
  aging: { status: StageAgingStatus; daysInStage: number | null; avgDays: number | null; ownerAvgDays: number | null; variance: number | null; attention: number; stalled: number; why: Why[] };
  po: {
    current: string | null; original: string | null; predicted: string | null; confidence: number; changes: number;
    daysUntil: number | null; overdue: boolean; warnings: string[]; why: Why[]; confidenceWhy: Why[];
  };
  completeness: { score: number; missing: string[]; checked: number; missingCritical: string[]; missingRecommended: string[]; present: string[]; band: CompletenessBand; gapKeys: string[] };
  quotation: {
    revisions: { revision: string; value: number; gp: number | null }[];
    original: { revision: string; value: number; gp: number | null } | null;
    current: { revision: string; value: number; gp: number | null } | null;
    reduction: number | null; discountPct: number | null; gpDrop: number | null;
    approvalLevel: ApprovalLevel; approvalStatus: string | null; warnings: string[]; why: Why[];
  };
  technical: { score: number | null; systems: { system: string; brand: string; score: number; missing: string[] }[]; warnings: string[] };
  tender: {
    applies: boolean; tracked: boolean; deadline: string | null; internalDeadline: string | null; daysLeft: number | null;
    items: { label: string; progress: number | null; text: string }[]; score: number | null; risk: Severity; action: string | null;
  };
}

const blank = (s: string | null | undefined) => !s || !s.trim();
export const addDays = (d: string, n: number) => { const x = new Date(`${d}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + Math.round(n)); return x.toISOString().slice(0, 10); };
export const dayDiff = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400_000);
const DUE: Record<Severity, number> = { critical: 0, high: 1, medium: 3, low: 7 };

export function runEngine(i: EngineInput, s: IntelSettings): EngineResult {
  const risks: DetectedRisk[] = [];
  const add = (key: string, description: string, action: string, responsible: Responsible = 'owner', dynamic?: Severity) => {
    const rule = riskRule(key);
    if (!rule) return;
    const severity = s.severity[key] ?? dynamic ?? rule.severity;
    if (severity === 'off') return;
    risks.push({ key, category: rule.category, severity, title: rule.label, description, action, responsible });
  };
  const idle = i.daysSinceActivity ?? i.daysSinceCreated;
  const quotation = quoteIntel(i, s);
  const technical = technicalReadiness(i, s);
  const tender = tenderReadiness(i, s, technical.tracked, quotation);

  // --- Stage aging (existing stages, thresholds per stage) ---
  const lim = { ...s.stageDefaults, ...(s.stages[i.stageId] ?? {}) };
  const agingWhy: Why[] = [];
  let aging: StageAgingStatus = 'normal';
  const d = i.daysInStage;
  if (d != null) {
    agingWhy.push({ text: `${d} days in ${i.stageName}` });
    if (i.stageAvgDays != null) agingWhy.push({ text: `Danat average in this stage: ${Math.round(i.stageAvgDays)} days` });
    if (d > lim.stalled) { aging = 'stalled'; agingWhy.push({ text: `Over the ${lim.stalled}-day stalled limit for this stage`, good: false }); }
    else if (d > lim.attention) { aging = 'attention'; agingWhy.push({ text: `Over the ${lim.attention}-day attention limit for this stage`, good: false }); }
    else if (i.stageAvgDays != null && d >= 7 && d > i.stageAvgDays * 1.25) { aging = 'slower'; agingWhy.push({ text: 'More than 25% above the Danat average', good: false }); }
    else agingWhy.push({ text: 'Within the normal time for this stage', good: true });
  }
  if (aging === 'stalled') add('stage_stalled', `${d} days in ${i.stageName} (limit ${lim.stalled}${i.stageAvgDays != null ? `, Danat average ${Math.round(i.stageAvgDays)}` : ''}).`, `Agree the next step with the customer to move the deal forward, or update its status.`);
  else if (aging === 'attention') add('stage_attention', `${d} days in ${i.stageName} (attention limit ${lim.attention}).`, 'Review what is holding the deal in this stage.');
  if (i.backwardMoves >= 2) add('moving_backward', `The deal moved back to an earlier stage ${i.backwardMoves} times.`, 'Check with the customer what changed and re-qualify the deal.');
  if (i.closeDate && i.closeDate < i.today) add('close_overdue', `Close date ${i.closeDate} passed ${dayDiff(i.closeDate, i.today)} days ago and the deal is still open.`, 'Update the close date or close the deal.');

  // --- Activity ---
  if (idle > s.idleDays) add('no_activity', `No customer activity for ${idle} days (limit ${s.idleDays}).`, 'Call or meet the customer and log it.');
  if (i.quoteSubmitted && i.daysSinceQuote != null && i.daysSinceQuote > s.quoteFollowUpDays && (i.daysSinceActivity == null || i.daysSinceActivity > s.quoteFollowUpDays)) {
    add('no_quote_followup', `Quotation submitted ${i.daysSinceQuote} days ago with no follow-up since.`, 'Follow up the submitted quotation with the customer.');
  }
  if (blank(i.nextStep)) add('no_next_action', 'No next action is recorded on the deal.', 'Set the next action and its date.');
  else if (i.nextActionDate && i.nextActionDate < i.today) add('next_action_overdue', `Next action “${i.nextStep}” was due ${i.nextActionDate}.`, 'Complete or reschedule the next action.');

  // --- Commercial ---
  const margin = quotation.current?.gp ?? i.expectedMargin;
  // A GP below target that management approved is decided: no more "escalate for approval" (it closes by itself).
  if (margin != null && margin < i.targetMargin && quotation.approvalStatus !== 'approved') add('low_margin', `Expected GP ${margin}% is below the ${i.targetMargin}% target.`, 'Escalate for pricing approval or ask the manufacturer for a better discount.', 'presales');
  if (i.valueDrop != null && i.valueDrop >= 0.15) add('value_drop', `Deal value dropped ${Math.round(i.valueDrop * 100)}% in the last 30 days.`, 'Confirm the scope and price with the customer.');
  if (i.pendingApproval) add('approval_pending', 'An approval request on this deal is waiting.', 'Ask the approver to decide.');
  if (i.quoteExpired) add('quote_expired', 'The latest quotation is past its validity date.', 'Renew the quotation validity with the customer.');

  // --- Technical ---
  if (i.submittalsToRevise > 0) add('submittal_revise', `${i.submittalsToRevise} submittal${i.submittalsToRevise === 1 ? '' : 's'} returned with code C or rejected.`, 'Revise and resubmit the technical submittal.', 'presales');
  // The customer asked for submittals: get them out fast, it's a deal we can win.
  if ((i.submittalRequestsLate ?? 0) > 0) add('submittal_request_late', `${i.submittalRequestsLate} submittal request${i.submittalRequestsLate === 1 ? ' is' : 's are'} past the date the customer asked for.`, 'Finish the submittal now and mark it ready, so sales can send it.', 'presales');
  else if ((i.submittalRequestsOpen ?? 0) > 0) add('submittal_requested', `The customer asked for ${i.submittalRequestsOpen} submittal${i.submittalRequestsOpen === 1 ? '' : 's'}: a strong buying signal.`, 'Prepare the submittal and mark it ready, so sales can send it.', 'presales');
  if (i.submittalsOverdue > 0) add('submittal_overdue', `${i.submittalsOverdue} submittal${i.submittalsOverdue === 1 ? ' is' : 's are'} past the consultant’s reply date.`, 'Follow up the consultant on the submittal reply.', 'presales');
  if (i.presalesStatus === 'clarification') add('clarification', 'Pre-sales is waiting for documents or answers.', 'Send pre-sales the missing documents.');
  if (!i.hasDocuments && i.daysSinceCreated > 3 && (i.presalesStatus ?? 'awaiting_documents') === 'awaiting_documents') add('no_documents', 'No tender documents uploaded yet.', 'Upload the tender documents (BOQ, drawings, specifications).');

  // --- Relationship ---
  if (!i.decisionMakerId) add('no_decision_maker', 'No decision maker is identified.', 'Identify the decision maker and add them to the deal.');
  if (i.relationshipStrength === 'weak') add('weak_relationship', 'The relationship with the customer is weak.', 'Arrange a meeting with the decision maker.');
  if (i.customerSentiment === 'negative' || i.customerSentiment === 'very_negative') add('negative_sentiment', 'Customer sentiment is negative.', 'Meet the customer to address their concerns.');

  // --- Timeline ---
  if (i.expectedPoDate && i.expectedPoDate < i.today) {
    const late = dayDiff(i.expectedPoDate, i.today);
    if (late > s.poSevereDays) add('po_severely_overdue', `Expected PO date ${i.expectedPoDate} passed ${late} days ago.`, 'Confirm the real PO date with the customer and update it.');
    else add('po_overdue', `Expected PO date ${i.expectedPoDate} passed ${late} days ago.`, 'Update the expected PO date with the customer.');
  } else if (!i.expectedPoDate && !i.isTender) add('no_po_date', 'No expected PO date.', 'Ask the customer when they expect to issue the PO.');
  // --- Quotation GP ---
  const ql = quotation.approvalLevel;
  if (ql !== 'none' && quotation.approvalStatus !== 'approved') {
    add('gp_approval', `Quotation ${quotation.current?.revision} GP ${quotation.current?.gp}% needs ${APPROVAL_LEVEL_LABEL[ql].toLowerCase()}${quotation.approvalStatus === 'rejected' ? ' (it was rejected)' : quotation.approvalStatus === 'pending' ? ' (waiting)' : ''}.`,
      quotation.approvalStatus === 'rejected' ? 'Revise the pricing and send a new revision.' : 'Get the GP approved before sending the quotation.', 'presales', ql === 'critical' ? 'critical' : ql === 'management' ? 'high' : 'medium');
  }
  if (quotation.gpDrop != null && quotation.gpDrop >= s.gpErosionPts) add('margin_erosion', `GP fell from ${quotation.original?.gp}% (${quotation.original?.revision}) to ${quotation.current?.gp}% (${quotation.current?.revision}).`, 'Review the price reductions with management before the next revision.', 'presales');
  if (quotation.discountPct != null && quotation.discountPct >= s.discountWarnPct) add('excessive_discount', `The quotation is ${quotation.discountPct}% below the original ${quotation.original?.revision}.`, 'Check the scope and the discount against the margin.', 'owner');
  if (quotation.revisions.length > s.maxRevisions) add('too_many_revisions', `The quotation has ${quotation.revisions.length} revisions.`, 'Agree the final scope and price with the customer.', 'owner');
  // --- Technical readiness ---
  const designOpen = technical.systems.filter((x) => x.missing.includes('Design'));
  if (designOpen.length) add('design_incomplete', `Design incomplete: ${designOpen.map((x) => x.system).join(', ')}.`, 'Complete the technical design.', 'presales');
  const vq = technical.warnings.find((w) => w.includes('vendor quotation'));
  if (vq) add('vendor_quotes_missing', vq, 'Follow up the vendor quotations.', 'presales');
  const clar = i.lines.filter((l) => l.confirmed && l.revision === i.currentRevision).reduce((n, l) => n + (l.clarificationsOpen || 0), 0);
  if (clar > 0) add('clarifications_open', `${clar} technical clarification${clar === 1 ? '' : 's'} unresolved.`, 'Close the open clarifications with the consultant.', 'presales');
  if (tender.tracked && (tender.risk === 'high' || tender.risk === 'critical')) add('tender_not_ready', `Tender package ${tender.score}% ready with ${tender.daysLeft} day${tender.daysLeft === 1 ? '' : 's'} left.`, tender.action ?? 'Complete the tender package.', 'presales', tender.risk);

  if (!tender.tracked && i.tenderDeadline && !i.quoteSubmitted && i.tenderDeadline >= i.today) {
    const left = dayDiff(i.today, i.tenderDeadline);
    if (left <= s.tenderCriticalDays) add('tender_critical', `Tender deadline ${i.tenderDeadline} is in ${left} day${left === 1 ? '' : 's'} and the quotation is not submitted.`, 'Complete the quotation before the tender deadline. Escalate anything missing now.', 'presales');
    else if (left <= s.tenderWarnDays) add('tender_soon', `Tender deadline ${i.tenderDeadline} is in ${left} days and the quotation is not submitted.`, 'Complete the quotation before the tender deadline.', 'presales');
  }
  if (i.internalDeadline && i.internalDeadline < i.today && !i.quoteSubmitted && i.presalesStatus !== 'quotation_ready') {
    add('internal_deadline_overdue', `The internal quotation deadline ${i.internalDeadline} passed.`, 'Finish the quotation and mark it ready.', 'presales');
  }

  // --- Completeness (weighted: critical 3, recommended 2, optional 1) ---
  let total = 0; let got = 0;
  const missing: string[] = []; const missingCritical: string[] = []; const missingRecommended: string[] = []; const present: string[] = [];
  const gapKeys: string[] = [];
  for (const f of COMPLETENESS_FIELDS) {
    const when = s.completeness[f.key] ?? f.when;
    const applies = when === 'always' || (when === 'direct' && !i.isTender) || (when === 'quoted' && i.stageQuoted) || (when === 'quoted_direct' && i.stageQuoted && !i.isTender) || (when === 'tender' && i.isTender);
    if (!applies) continue;
    const weight = s.completenessWeights[f.key] ?? f.weight;
    total += W_POINTS[weight];
    if (i.filled[f.key]) { got += W_POINTS[weight]; present.push(f.label); continue; }
    missing.push(f.label);
    if (weight === 'critical') { missingCritical.push(f.label); gapKeys.push(f.key); } else if (weight === 'recommended') missingRecommended.push(f.label);
  }
  const score = total ? Math.round((got / total) * 100) : 100;
  const b = s.completenessBands;
  const band: CompletenessBand = score >= b.excellent ? 'excellent' : score >= b.good ? 'good' : score >= b.improve ? 'improve' : 'poor';
  const completeness = { score, missing, checked: missing.length + present.length, missingCritical, missingRecommended, present, band, gapKeys };

  // --- Agent: further risks from history, engagement and information quality ---
  if (i.poChanges >= s.poChangesWarn) add('po_postponed', `The expected PO date was moved ${i.poChanges} times.`, 'Confirm the real PO timeline with the decision maker.');
  if (i.engagement && i.engagement.prev30 >= 3 && i.engagement.last30 <= i.engagement.prev30 / 2) {
    add('engagement_declining', `Customer activity fell from ${i.engagement.prev30} to ${i.engagement.last30} in the last 30 days.`, 'Re-engage the customer: book a meeting with the decision maker.');
  }
  const large = (i.amount ?? 0) >= s.mgmtValue;
  if (large && score < b.improve) add('weak_information', `A ${Math.round((i.amount ?? 0) / 1000).toLocaleString('en-US')}K deal is only ${score}% complete (missing: ${missingCritical.slice(0, 3).join(', ') || missing.slice(0, 3).join(', ')}).`, 'Complete the critical information on the deal.');

  risks.sort((a, b2) => rank(b2.severity) - rank(a.severity));
  const highs = risks.filter((r) => r.severity === 'high').length;
  const top = risks[0]?.severity ?? 'low';
  const overallRisk: Severity = top === 'critical' || highs >= 3 ? 'critical' : top;
  const overallWhy: Why[] = risks.length
    ? [{ text: `${risks.length} risk${risks.length === 1 ? '' : 's'} detected; the most severe is ${SEVERITY_LABEL[top].toLowerCase()}.` },
       ...(top !== 'critical' && highs >= 3 ? [{ text: `${highs} high risks together make the deal critical.`, good: false }] : [])]
    : [{ text: 'No risks detected.', good: true }];

  // --- Positive signals ---
  const positives: Why[] = (i.signals ?? []).map((text) => ({ text, good: true }));

  // --- Supporting evidence for recommendations (what the CRM knows about the deal) ---
  const evidence = [
    i.amount ? `Deal value SAR ${Math.round(i.amount).toLocaleString('en-US')}` : null,
    `Stage: ${i.stageName}${i.daysInStage != null ? ` (${i.daysInStage} days)` : ''}`,
    i.quoteSubmitted ? 'Quotation submitted' : null,
    i.decisionMakerId ? 'Decision maker identified' : 'Decision maker not identified',
    i.isTender ? 'Tender' : null,
    i.expectedPoDate ? `Expected PO ${i.expectedPoDate}` : i.isTender ? null : 'No expected PO date',
    i.tenderDeadline && !i.quoteSubmitted ? `Tender deadline ${i.tenderDeadline}` : null,
  ].filter((x): x is string => !!x).join(' · ');

  // --- Next Best Action: the risks as actions, plus information gaps worth closing ---
  const actions: RecommendedAction[] = risks.map((r) => ({ key: r.key, action: r.action, reason: r.description, priority: r.severity, dueInDays: DUE[r.severity], responsible: r.responsible, evidence }));
  // Tenders have no customer budget to confirm (the price is set by the tender).
  // No "Confirm the customer budget" recommendation: customers often don't share their budget.
  if (i.decisionMakerId && idle > 7 && !risks.some((r) => r.key === 'no_activity')) actions.push({ key: 'contact_dm', action: 'Contact the decision maker.', reason: `No customer activity for ${idle} days.`, priority: 'low', dueInDays: 7, responsible: 'owner', evidence });

  // --- Management intervention: large deals where a manager can change the outcome ---
  if (large) {
    const has = (k: string) => risks.some((r) => r.key === k);
    const mgmt = (key: string, action: string, reason: string, priority: Severity) => actions.push({ key, action, reason, priority, dueInDays: DUE[priority], responsible: 'owner', evidence, management: true });
    if (has('excessive_discount')) mgmt('mgmt_vendor_discount', 'Escalate a final discount with the manufacturer.', 'A large discount from the original quotation on a large deal.', 'high');
    // Once management approved this quotation's GP, the decision is made: no more "decide" action (even if the GP
    // stays below the target).
    if (has('gp_approval') || (has('low_margin') && quotation.approvalStatus !== 'approved')) mgmt('mgmt_commercial_approval', 'Decide the commercial approval (price / GP).', 'The margin needs a management decision.', 'high');
    if (has('submittal_revise')) mgmt('mgmt_consultant_escalation', 'Escalate with the consultant (technical meeting).', 'Technical rejection on a large deal.', 'critical');
    if (has('no_decision_maker') && i.stageQuoted) mgmt('mgmt_executive_meeting', 'Arrange an executive meeting with the customer.', 'Quoted without access to the decision maker.', 'high');
    if (has('tender_critical') || has('tender_not_ready') || has('internal_deadline_overdue')) mgmt('mgmt_resources', 'Add pre-sales resources to meet the deadline.', 'The tender package is late.', 'critical');
  }
  // Same priority: the deal team's own action first, then the management intervention.
  actions.sort((a, b2) => rank(b2.priority) - rank(a.priority) || Number(!!a.management) - Number(!!b2.management));

  // --- Expected PO intelligence ---
  const po = poIntel(i, s, idle);

  const variance = d != null && i.stageAvgDays != null ? Math.round(d - i.stageAvgDays) : null;
  return {
    risks, overallRisk, overallWhy, actions, positives,
    aging: { status: aging, daysInStage: d, avgDays: i.stageAvgDays == null ? null : Math.round(i.stageAvgDays), ownerAvgDays: i.ownerStageAvgDays == null ? null : Math.round(i.ownerStageAvgDays), variance, attention: lim.attention, stalled: lim.stalled, why: agingWhy },
    po, completeness, quotation, technical: { score: technical.score, systems: technical.systems, warnings: technical.warnings }, tender,
  };
}
function poIntel(i: EngineInput, s: IntelSettings, idle: number): EngineResult['po'] {
  const why: Why[] = [];
  const confidenceWhy: Why[] = [];
  const warnings: string[] = [];
  const current = i.expectedPoDate;

  // Predicted date: the salesperson's date (or the stage-history estimate), moved by what usually delays deals.
  const stageEstimate = i.remainingStageDays != null ? addDays(i.today, i.remainingStageDays) : null;
  let base = current ?? stageEstimate ?? i.closeDate;
  if (current) why.push({ text: `Salesperson expects the PO on ${current}` });
  else if (stageEstimate) why.push({ text: `No PO date: deals at this stage usually need ${Math.round(i.remainingStageDays!)} more days` });
  else if (i.closeDate) why.push({ text: `No PO date: using the close date ${i.closeDate}` });
  let shift = 0;
  const push = (n: number, text: string) => { if (n > 0) { shift += n; why.push({ text, effect: `+${n} days`, good: false }); } };
  if (base) {
    if (i.ownerPoSlipDays != null && i.ownerPoSlipDays > 3) push(Math.round(i.ownerPoSlipDays), `This salesperson’s past PO dates came ${Math.round(i.ownerPoSlipDays)} days late on average`);
    if (!i.decisionMakerId) push(7, 'No decision maker identified');
    if (idle > s.idleDays) push(14, `No customer activity for ${idle} days`);
    if (i.poChanges >= 2) push(7, `PO date already moved ${i.poChanges} times`);
    let predicted = addDays(base, shift);
    if (stageEstimate && stageEstimate > predicted) { predicted = stageEstimate; why.push({ text: `Stage history: deals at this stage need about ${Math.round(i.remainingStageDays!)} more days`, good: false }); }
    if (predicted <= i.today) { predicted = addDays(i.today, 14); why.push({ text: 'The date has passed: at least two weeks from today', good: false }); }
    base = predicted;
  }
  const predicted = base;

  // Confidence: starts at 50 and moves with what is known about the deal.
  let c = 50;
  const conf = (n: number, text: string) => { c += n; confidenceWhy.push({ text, effect: `${n > 0 ? '+' : ''}${n}%`, good: n > 0 }); };
  if (!current) conf(-10, 'No expected PO date from the salesperson');
  if (i.decisionMakerId) conf(10, 'Decision maker identified');
  if (i.quoteSubmitted) conf(10, 'Quotation submitted');
  if (i.submittalsRequested) conf(10, 'Customer asked for submittals');
  if (['strong', 'very_strong'].includes(i.relationshipStrength ?? '')) conf(5, 'Strong customer relationship');
  if (i.daysSinceActivity != null && i.daysSinceActivity <= 7) conf(5, 'Recent customer activity');
  if (idle > s.idleDays) conf(-10, `No customer activity for ${idle} days`);
  if (i.poChanges > 1) conf(-Math.min(30, (i.poChanges - 1) * 10), `PO date changed ${i.poChanges} times`);
  if (current && current < i.today) conf(-20, 'Expected PO date already passed');
  if (current && predicted) {
    const gap = Math.abs(dayDiff(current, predicted));
    if (gap > 7) conf(-Math.min(15, Math.round(gap / 7)), `System prediction is ${gap} days from the salesperson’s date`);
  }
  const confidence = Math.max(5, Math.min(95, c));

  const daysUntil = current ? dayDiff(i.today, current) : null;
  const overdue = !!current && current < i.today;
  if (i.poChanges >= s.poChangesWarn) warnings.push(`Expected PO changed ${i.poChanges} times.`);
  if (overdue) warnings.push('Expected PO is overdue.');
  if (current && predicted && Math.abs(dayDiff(current, predicted)) > 14) warnings.push('System prediction differs from the salesperson’s forecast.');
  if (i.previousPoConfidence != null && confidence < i.previousPoConfidence - 5) warnings.push(`PO confidence is decreasing (was ${i.previousPoConfidence}%).`);
  return { current, original: i.originalPoDate ?? current, predicted, confidence, changes: i.poChanges, daysUntil, overdue, warnings, why, confidenceWhy };
}

// ---------------------------------------------------------------------------
// Quotation GP (per system, on Our Quoted Brands), technical readiness and tender readiness
// ---------------------------------------------------------------------------
const sysLabel = (v: string) => QUOTE_SYSTEMS.find((x) => x.value === v)?.label ?? v;
const round1 = (n: number) => Math.round(n * 10) / 10;

/** The deal's quotation: value and value-weighted GP of each revision (main offers), original against current. */
export function quoteIntel(i: Pick<EngineInput, 'lines' | 'currentRevision' | 'revisionOrder' | 'approval'>, s: IntelSettings): EngineResult['quotation'] {
  const rev = (r: string) => {
    const ls = i.lines.filter((l) => l.revision === r && l.confirmed && l.offerType === 'main');
    const value = ls.reduce((n, l) => n + (l.packageValue ?? 0), 0);
    const withGp = ls.filter((l) => l.gpPct != null);
    const valued = withGp.filter((l) => (l.packageValue ?? 0) > 0);
    const gp = valued.length
      ? round1(valued.reduce((n, l) => n + l.packageValue! * l.gpPct!, 0) / valued.reduce((n, l) => n + l.packageValue!, 0))
      : withGp.length ? round1(withGp.reduce((n, l) => n + l.gpPct!, 0) / withGp.length) : null;
    return { revision: r, value, gp };
  };
  const revisions = i.revisionOrder.map(rev).filter((r) => r.value > 0 || r.gp != null);
  const current = i.currentRevision ? rev(i.currentRevision) : null;
  const cur = current && (current.value > 0 || current.gp != null) ? current : null;
  const original = revisions[0] ?? null;
  const why: Why[] = [];
  const warnings: string[] = [];
  let reduction: number | null = null; let discountPct: number | null = null; let gpDrop: number | null = null;
  if (cur && original && original.revision !== cur.revision) {
    if (original.value > 0 && cur.value > 0) {
      reduction = original.value - cur.value;
      discountPct = reduction > 0 ? round1((reduction / original.value) * 100) : 0;
    }
    if (original.gp != null && cur.gp != null) gpDrop = round1(original.gp - cur.gp);
  }
  let approvalLevel: ApprovalLevel = 'none';
  if (cur?.gp != null) {
    approvalLevel = cur.gp >= s.gpNormal ? 'none' : cur.gp >= s.gpManager ? 'manager' : cur.gp >= s.gpManagement ? 'management' : 'critical';
    why.push({ text: `Current GP ${cur.gp}% (limits: ${s.gpNormal}% normal, ${s.gpManager}% sales manager, ${s.gpManagement}% management)` });
    why.push({ text: APPROVAL_LEVEL_LABEL[approvalLevel], good: approvalLevel === 'none' });
  } else why.push({ text: 'No GP % recorded on the systems of the current revision yet' });
  const approvalStatus = approvalLevel === 'none' ? null : i.approval && i.approval.level === approvalLevel ? i.approval.status : 'pending';
  if (gpDrop != null && gpDrop >= s.gpErosionPts) warnings.push(`Margin erosion: GP down ${gpDrop} points since ${original!.revision}.`);
  if (discountPct != null && discountPct >= s.discountWarnPct) warnings.push(`Price ${discountPct}% below the original quotation.`);
  if (revisions.length > s.maxRevisions) warnings.push(`${revisions.length} revisions.`);
  if (approvalLevel !== 'none' && approvalStatus !== 'approved') warnings.push(`${APPROVAL_LEVEL_LABEL[approvalLevel]} ${approvalStatus === 'rejected' ? 'was refused' : 'is needed'}.`);
  return { revisions, original, current: cur, reduction, discountPct, gpDrop, approvalLevel, approvalStatus, warnings, why };
}

const progressOf = (key: ReadinessItem, l: EngineLine): number | null | undefined => {
  if (key === 'technical') return ({ not_submitted: 0, pending: 0.6, approved: 1, rejected: 0 } as Record<string, number>)[l.technicalApproval ?? 'not_submitted'] ?? 0;
  const col = READINESS_ITEMS.find((r) => r.key === key)!.column as keyof EngineLine;
  const v = l[col] as string | null;
  if (v == null) return undefined; // not recorded
  return READINESS_STATUSES.find((x) => x.value === v)?.progress ?? 0;
};

/** Technical readiness 0–100 of the current revision's systems; only once pre-sales records readiness on a system. */
export function technicalReadiness(i: Pick<EngineInput, 'lines' | 'currentRevision'>, s: IntelSettings) {
  const lines = i.lines.filter((l) => l.confirmed && l.revision === i.currentRevision);
  const tracked = lines.some((l) => [l.boqStatus, l.designStatus, l.drawingsStatus, l.complianceStatus, l.vendorQuoteStatus, l.costingStatus].some((x) => x != null));
  if (!tracked) return { tracked: false, score: null as number | null, systems: [] as { system: string; brand: string; score: number; missing: string[] }[], warnings: [] as string[], items: {} as Record<string, number | null> };
  const systems = lines.map((l) => {
    let w = 0; let got = 0; const missing: string[] = [];
    for (const item of READINESS_ITEMS) {
      const p = progressOf(item.key, l);
      if (p === null) continue; // not required
      const weight = s.readinessWeights[item.key] ?? 0;
      w += weight; got += weight * (p ?? 0);
      if ((p ?? 0) < 1) missing.push(item.label === 'BOQ review' ? 'BOQ' : item.label.split(' ')[0]!);
    }
    return { system: sysLabel(l.system), brand: l.brand, score: w ? Math.round((got / w) * 100) : 100, missing };
  });
  const warnings: string[] = [];
  for (const l of lines) {
    if (['not_started', 'in_progress', 'pending_info'].includes(l.designStatus ?? '')) warnings.push(`${sysLabel(l.system)} design incomplete.`);
    if (l.technicalApproval === 'pending') warnings.push(`${sysLabel(l.system)} consultant approval pending.`);
    if (l.technicalApproval === 'rejected') warnings.push(`${sysLabel(l.system)} rejected by the consultant.`);
  }
  const vqNeeded = lines.filter((l) => l.vendorQuoteStatus != null && l.vendorQuoteStatus !== 'not_required');
  const vqMissing = vqNeeded.filter((l) => !['approved', 'completed', 'approved_with_comments'].includes(l.vendorQuoteStatus ?? ''));
  if (vqMissing.length) warnings.push(`${vqMissing.length} vendor quotation${vqMissing.length === 1 ? '' : 's'} missing (${vqMissing.map((l) => sysLabel(l.system)).join(', ')}).`);
  const clar = lines.reduce((n, l) => n + (l.clarificationsOpen || 0), 0);
  if (clar) warnings.push(`${clar} clarification${clar === 1 ? '' : 's'} unresolved.`);
  const avgItem = (key: ReadinessItem) => {
    const ps = lines.map((l) => progressOf(key, l)).filter((p): p is number => p != null);
    return ps.length ? ps.reduce((a, b) => a + b, 0) / ps.length : null;
  };
  const items = Object.fromEntries(READINESS_ITEMS.map((r) => [r.key, avgItem(r.key)])) as Record<string, number | null>;
  const score = systems.length ? Math.round(systems.reduce((n, x) => n + x.score, 0) / systems.length) : null;
  return { tracked: true, score, systems: systems.sort((a, b) => a.score - b.score), warnings, items: { ...items, vendorQuotesNeeded: vqNeeded.length, vendorQuotesMissing: vqMissing.length } };
}

/** Tender readiness 0–100 and delivery risk, from the deadline and how complete the tender package is. */
export function tenderReadiness(i: EngineInput, s: IntelSettings, tracked: boolean, q: EngineResult['quotation']): EngineResult['tender'] {
  const deadline = i.tenderDeadline;
  const applies = i.isTender || !!deadline;
  const daysLeft = deadline ? dayDiff(i.today, deadline) : null;
  if (!applies) return { applies, tracked: false, deadline, internalDeadline: i.internalDeadline, daysLeft, items: [], score: null, risk: 'low', action: null };
  const tech = technicalReadiness(i, s);
  const pct = (p: number | null | undefined) => (p == null ? null : Math.round(p * 100));
  const it = tech.items;
  const vqNeeded = (it.vendorQuotesNeeded as number | undefined) ?? 0;
  const vqMissing = (it.vendorQuotesMissing as number | undefined) ?? 0;
  const commercial = i.quoteSubmitted || i.presalesStatus === 'quotation_ready' ? 1 : i.presalesStatus === 'in_progress' ? 0.5 : 0;
  const approval = q.approvalLevel === 'none' || q.approvalStatus === 'approved' ? 1 : q.approvalStatus === 'rejected' ? 0 : 0.5;
  const items: EngineResult['tender']['items'] = [
    { label: 'BOQ', progress: pct(it.boq), text: it.boq == null ? 'Not recorded' : `${pct(it.boq)}%` },
    { label: 'Design', progress: pct(it.design), text: it.design == null ? 'Not recorded' : `${pct(it.design)}%` },
    { label: 'Drawings', progress: pct(it.drawings), text: it.drawings == null ? 'Not recorded' : `${pct(it.drawings)}%` },
    { label: 'Vendor quotations', progress: vqNeeded ? Math.round(((vqNeeded - vqMissing) / vqNeeded) * 100) : null, text: vqNeeded ? `${vqNeeded - vqMissing}/${vqNeeded}` : 'Not recorded' },
    { label: 'Technical proposal', progress: pct(it.compliance), text: it.compliance == null ? 'Not recorded' : `${pct(it.compliance)}%` },
    { label: 'Costing', progress: pct(it.costing), text: it.costing == null ? 'Not recorded' : it.costing >= 1 ? 'Completed' : 'Incomplete' },
    { label: 'Commercial proposal', progress: Math.round(commercial * 100), text: commercial >= 1 ? 'Ready' : commercial > 0 ? 'In progress' : 'Not ready' },
    { label: 'Management approval', progress: Math.round(approval * 100), text: q.approvalLevel === 'none' ? 'Not needed' : q.approvalStatus === 'approved' ? 'Approved' : q.approvalStatus === 'rejected' ? 'Refused' : 'Waiting' },
    { label: 'Submission', progress: i.quoteSubmitted ? 100 : 0, text: i.quoteSubmitted ? 'Submitted' : 'Not submitted' },
  ];
  const known = items.filter((x) => x.progress != null && x.label !== 'Submission');
  const score = i.quoteSubmitted ? 100 : tracked && known.length ? Math.round(known.reduce((n, x) => n + x.progress!, 0) / known.length) : null;
  let risk: Severity = 'low';
  if (!i.quoteSubmitted && daysLeft != null && score != null) {
    if (daysLeft < 0) risk = 'critical';
    else if (daysLeft <= s.tenderCriticalDays && score < 90) risk = 'critical';
    else if (daysLeft <= s.tenderWarnDays && score < 75) risk = 'high';
    else if (daysLeft <= 14 && score < 50) risk = 'medium';
  }
  const open = items.filter((x) => x.label !== 'Submission' && x.progress != null && x.progress < 100).map((x) => x.label.toLowerCase());
  const action = i.quoteSubmitted ? null : open.length
    ? `${risk === 'critical' || risk === 'high' ? 'Immediately complete' : 'Complete'} ${open.slice(0, 3).join(', ')}${open.length > 3 ? ' and the rest of the package' : ''} before the deadline.`
    : 'Submit the quotation.';
  return { applies, tracked: tracked && score != null, deadline, internalDeadline: i.internalDeadline, daysLeft, items, score, risk, action };
}

/** Business impact × risk severity × urgency, for ranking deals that need attention (bigger first). */
export function attentionRank(amount: number | null, risk: Severity, dueInDays: number, probability: number | null = null): number {
  const impact = Math.log10(Math.max(10_000, amount ?? 0));
  const severity = [1, 2, 4, 8][rank(risk)]!;
  const urgency = dueInDays <= 0 ? 3 : dueInDays <= 1 ? 2.5 : dueInDays <= 3 ? 1.5 : 1;
  // Closing probability: a likely deal matters more (0.5× at 0%, 1× at 100%; unknown counts as 50%).
  const likely = 0.5 + (probability ?? 50) / 200;
  return Math.round(impact * severity * urgency * likely * 100) / 100;
}

// ---------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------
export interface DealRiskRow { id: string; key: string; category: RiskCategory; severity: Severity; title: string; description: string; action: string; responsibleName: string | null; status: 'open' | 'resolved'; detectedAt: string; resolvedAt: string | null; acknowledgedByName: string | null; acknowledgedAt: string | null }
export interface DealActionRow {
  id: string; key: string; action: string; reason: string; priority: Severity; dueDate: string; responsibleId: string | null; responsibleName: string | null;
  status: 'open' | 'accepted' | 'converted' | 'snoozed' | 'dismissed' | 'replaced' | 'done'; snoozedUntil: string | null; dismissReason: string | null; taskId: string | null;
  decidedByName: string | null; decidedAt: string | null; createdAt: string;
  management: boolean; evidence: string | null; assignedByName: string | null; userAction: string | null; outcome: string | null;
}
export interface DealEngineView {
  result: EngineResult;
  risks: DealRiskRow[];
  actions: DealActionRow[];
  stageHistory: { stageName: string; enteredAt: string; leftAt: string | null; days: number; backward: boolean }[];
  poHistory: { oldDate: string | null; newDate: string | null; reason: string | null; changedByName: string | null; changedAt: string }[];
  totalAgeDays: number;
  canAct: boolean;
  approvals: { id: string; revision: string; level: ApprovalLevel; gpPct: number | null; value: number | null; status: 'pending' | 'approved' | 'rejected' | 'cancelled'; requestedAt: string; decidedByName: string | null; decidedAt: string | null; comment: string | null; canDecide: boolean }[];
  /** Commercial risk health derived from computeIntel (red only for genuine commercial risks). */
  riskHealth: 'green' | 'yellow' | 'red';
  /** Data completeness band from the engine's completeness analysis. */
  completenessHealth: CompletenessBand | null;
  /** How much commercial evidence is available for the risk classification. */
  evidenceSufficiency: 'sufficient' | 'partial' | 'insufficient';
  /** Per-factor score breakdown with explanations. */
  breakdown: { key: string; label: string; points: number; max: number; explanation: string }[];
}
export const actionDecisionSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('accept') }),
  // Done by hand (e.g. the meeting took place): closes it now; an optional note says what was done.
  z.object({ decision: z.literal('done'), note: z.string().trim().max(500).optional() }),
  z.object({ decision: z.literal('assign'), userId: z.string().uuid('Choose who') }),
  z.object({ decision: z.literal('replace'), action: z.string().trim().min(3, 'Describe your action').max(500), dueDate: isoDate.optional() }),
  z.object({ decision: z.literal('task'), dueDate: isoDate.optional() }),
  z.object({ decision: z.literal('snooze'), until: isoDate }),
  z.object({ decision: z.literal('dismiss'), reason: z.string().trim().min(3, 'Say why it does not apply').max(500) }),
]);
export const poChangeSchema = z.object({
  date: isoDate,
  reason: z.string().trim().min(3, 'Give the reason for the change').max(500),
}).strict();
